import React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Icon,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  TablePagination,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import {
  StockAdjustment,
  StockAdjustmentFilters,
  StockAdjustmentFormData,
  ADJUSTMENT_TYPE_OPTIONS,
  ADJUSTMENT_STATUS_OPTIONS,
  AdjustmentType,
} from "../../types/stock-adjustment.types";
import { StockAdjustmentForm } from "../../components/StockAdjustmentForm/StockAdjustmentForm";
import { stockAdjustmentService } from "../../services/stock-adjustment.service";
import { useDebounce } from "../../hooks/useDebounce";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import { ActionConfirmationDialog } from "../../components/ActionConfirmationDialog/ActionConfirmationDialog";
import { RejectionReasonDialog } from "../../components/RejectionReasonDialog/RejectionReasonDialog";

export const StockAdjustmentPage: React.FC = () => {
  const [adjustments, setAdjustments] = React.useState<StockAdjustment[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = React.useState<
    StockAdjustment | undefined
  >(undefined);
  const [formLoading, setFormLoading] = React.useState(false);

  // Notification state
  const [notification, setNotification] = React.useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);

  // Dialog states
  const [approveDialog, setApproveDialog] = React.useState<{
    open: boolean;
    adjustment: StockAdjustment | null;
    loading: boolean;
  }>({
    open: false,
    adjustment: null,
    loading: false,
  });

  const [rejectDialog, setRejectDialog] = React.useState<{
    open: boolean;
    adjustment: StockAdjustment | null;
    loading: boolean;
  }>({
    open: false,
    adjustment: null,
    loading: false,
  });

  // Auto-dismiss notifications after 5 seconds
  React.useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
    // Return undefined for all other code paths
    return undefined;
  }, [notification]);

  // Filters
  const [search, setSearch] = React.useState("");
  const [adjustmentType, setAdjustmentType] = React.useState<
    AdjustmentType | "all"
  >("all");
  const [status, setStatus] = React.useState<
    "pending" | "approved" | "rejected" | "all"
  >("all");
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);

  // Pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);
  const [totalItems, setTotalItems] = React.useState(0);

  const debouncedSearch = useDebounce(search, 500);

  // Mock current user - in real app, this would come from auth context
  const currentUser = {
    id: "user_123",
    name: "John Doe",
    role: "inventory_manager",
    isAdmin: true, // Set to true for demo purposes
  };

  const loadAdjustments = React.useCallback(async () => {
    try {
      setLoading(true);

      // Build filters object, only including fields with values
      const filters: StockAdjustmentFilters = {};

      // Only add search if it has a value
      if (debouncedSearch && debouncedSearch.trim()) {
        filters.search = debouncedSearch;
      }

      // Only add adjustment_type if it's not "all"
      if (adjustmentType !== "all") {
        filters.adjustment_type = adjustmentType;
      }

      // Only add status if it's not "all"
      if (status !== "all") {
        filters.status = status;
      }

      // Only add dates if they have values
      if (startDate) {
        filters.start_date = startDate.getTime();
      }
      if (endDate) {
        filters.end_date = endDate.getTime();
      }

      // Build request parameters
      const requestParams: any = {
        filters,
        pageIndex: page,
        pageSize: rowsPerPage,
        sortField: "created_at",
        sortOrder: "desc",
      };

      // Only add searchQuery if it's not empty (for global search)
      if (debouncedSearch && debouncedSearch.trim()) {
        requestParams.searchQuery = debouncedSearch;
      }

      const response = await stockAdjustmentService.listStockAdjustments(
        requestParams
      );

      console.log("Full list response:", response);
      console.log("Response data:", response.data);

      // Handle the nested data structure: {success: true, data: {items: [...], total: 1}}
      const responseData = (response.data as any).data || response.data;
      console.log("Parsed response data:", responseData);

      setAdjustments(responseData.items || []);
      setTotalItems(responseData.total || 0);
    } catch (error) {
      console.error("Error loading stock adjustments:", error);
      // Ensure adjustments is always an array even on error
      setAdjustments([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearch,
    adjustmentType,
    status,
    startDate,
    endDate,
    page,
    rowsPerPage,
  ]);

  React.useEffect(() => {
    loadAdjustments();
  }, [loadAdjustments]);

  const handleFormSubmit = async (values: StockAdjustmentFormData) => {
    console.log("Parent handleFormSubmit called with values:", values);

    try {
      setFormLoading(true);

      if (selectedAdjustment) {
        console.log("Updating existing adjustment...");
        await stockAdjustmentService.updateStockAdjustment(
          selectedAdjustment.id,
          values
        );
        setNotification({
          type: "success",
          message: "Stock adjustment updated successfully!",
        });
      } else {
        console.log("Creating new adjustment...");
        const response = await stockAdjustmentService.createStockAdjustment(
          values
        );
        console.log("Create response:", response);
        setNotification({
          type: "success",
          message: "Stock adjustment created successfully!",
        });
      }

      console.log("Closing form and refreshing data...");
      setIsFormOpen(false);
      setSelectedAdjustment(undefined);
      loadAdjustments();
    } catch (error: any) {
      console.error("Error saving stock adjustment:", error);
      console.error("Error details:", error?.response?.data);
      setNotification({
        type: "error",
        message:
          error?.response?.data?.error ||
          "Failed to save stock adjustment. Please try again.",
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (adjustment: StockAdjustment) => {
    setSelectedAdjustment(adjustment);
    setIsFormOpen(true);
  };

  const handleDelete = async (adjustment: StockAdjustment) => {
    if (
      window.confirm("Are you sure you want to delete this stock adjustment?")
    ) {
      try {
        await stockAdjustmentService.deleteStockAdjustment(adjustment.id);
        setNotification({
          type: "success",
          message: "Stock adjustment deleted successfully!",
        });
        loadAdjustments();
      } catch (error: any) {
        console.error("Error deleting stock adjustment:", error);
        setNotification({
          type: "error",
          message:
            error?.response?.data?.error ||
            "Failed to delete stock adjustment. Please try again.",
        });
      }
    }
  };

  const handleApprove = (adjustment: StockAdjustment) => {
    setApproveDialog({
      open: true,
      adjustment,
      loading: false,
    });
  };

  const handleConfirmApprove = async () => {
    if (!approveDialog.adjustment) return;

    try {
      setApproveDialog((prev) => ({ ...prev, loading: true }));

      await stockAdjustmentService.approveStockAdjustment(
        approveDialog.adjustment.id
      );

      setNotification({
        type: "success",
        message: "Stock adjustment approved successfully!",
      });

      loadAdjustments();

      // Close dialog
      setApproveDialog({
        open: false,
        adjustment: null,
        loading: false,
      });
    } catch (error: any) {
      console.error("Error approving stock adjustment:", error);
      setNotification({
        type: "error",
        message:
          error?.response?.data?.error ||
          "Failed to approve stock adjustment. Please try again.",
      });

      // Keep dialog open but stop loading
      setApproveDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleReject = (adjustment: StockAdjustment) => {
    setRejectDialog({
      open: true,
      adjustment,
      loading: false,
    });
  };

  const handleConfirmReject = async (reason: string) => {
    if (!rejectDialog.adjustment) return;

    try {
      setRejectDialog((prev) => ({ ...prev, loading: true }));

      await stockAdjustmentService.rejectStockAdjustment(
        rejectDialog.adjustment.id,
        reason
      );

      setNotification({
        type: "success",
        message: "Stock adjustment rejected successfully!",
      });

      loadAdjustments();

      // Close dialog
      setRejectDialog({
        open: false,
        adjustment: null,
        loading: false,
      });
    } catch (error: any) {
      console.error("Error rejecting stock adjustment:", error);
      setNotification({
        type: "error",
        message:
          error?.response?.data?.error ||
          "Failed to reject stock adjustment. Please try again.",
      });

      // Keep dialog open but stop loading
      setRejectDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleNewAdjustment = () => {
    setSelectedAdjustment(undefined);
    setIsFormOpen(true);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: StockAdjustment["status"]) => {
    const statusOption = ADJUSTMENT_STATUS_OPTIONS.find(
      (opt) => opt.value === status
    );
    return statusOption?.color || "default";
  };

  const getAdjustmentTypeLabel = (type: AdjustmentType) => {
    const typeOption = ADJUSTMENT_TYPE_OPTIONS.find(
      (opt) => opt.value === type
    );
    return typeOption?.label || type;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Notification */}
        {notification && (
          <Alert
            severity={notification.type}
            onClose={() => setNotification(null)}
            sx={{ mb: 3 }}
          >
            {notification.message}
          </Alert>
        )}

        <PageHeader
          title="Stock Adjustments"
          subtitle="Manage and track all stock adjustments"
          actions={
            <Button
              variant="contained"
              startIcon={<Icon>tune</Icon>}
              onClick={handleNewAdjustment}
              size="large"
            >
              Adjust Stock
            </Button>
          }
        />

        {/* Filters */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Search by item name, SKU, or reference..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>search</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Adjustment Type</InputLabel>
                <Select
                  value={adjustmentType}
                  label="Adjustment Type"
                  onChange={(e) =>
                    setAdjustmentType(e.target.value as AdjustmentType | "all")
                  }
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {ADJUSTMENT_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) =>
                    setStatus(
                      e.target.value as
                        | "pending"
                        | "approved"
                        | "rejected"
                        | "all"
                    )
                  }
                >
                  <MenuItem value="all">All Status</MenuItem>
                  {ADJUSTMENT_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearch("");
                  setAdjustmentType("all");
                  setStatus("all");
                  setStartDate(null);
                  setEndDate(null);
                  setPage(0);
                }}
                sx={{ height: 56 }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Icon color="primary">pending_actions</Icon>
                  <Box>
                    <Typography variant="h6">
                      {
                        (adjustments || []).filter(
                          (a) => a.status === "pending"
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pending
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Icon color="success">check_circle</Icon>
                  <Box>
                    <Typography variant="h6">
                      {
                        (adjustments || []).filter(
                          (a) => a.status === "approved"
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Approved
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Icon color="error">cancel</Icon>
                  <Box>
                    <Typography variant="h6">
                      {
                        (adjustments || []).filter(
                          (a) => a.status === "rejected"
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Rejected
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Icon color="info">inventory</Icon>
                  <Box>
                    <Typography variant="h6">{totalItems}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Adjustments
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Adjustments Table */}
        <Paper elevation={1}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography>Loading adjustments...</Typography>
            </Box>
          ) : (adjustments || []).length > 0 ? (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Reference</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Warehouse</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Authorized By</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(adjustments || []).map((adjustment) => (
                      <TableRow key={adjustment.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {adjustment.reference_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {adjustment.item_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              SKU: {adjustment.item_sku}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getAdjustmentTypeLabel(
                              adjustment.adjustment_type
                            )}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {adjustment.quantity} {adjustment.unit}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {adjustment.warehouse_name}
                            </Typography>
                            {adjustment.location_name && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {adjustment.location_name}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {format(
                              new Date(adjustment.adjustment_date),
                              "MMM dd, yyyy"
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              ADJUSTMENT_STATUS_OPTIONS.find(
                                (s) => s.value === adjustment.status
                              )?.label
                            }
                            size="small"
                            color={getStatusColor(adjustment.status) as any}
                            variant={
                              adjustment.status === "pending"
                                ? "outlined"
                                : "filled"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {adjustment.authorized_by_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            {adjustment.status === "pending" && (
                              <>
                                <Tooltip title="Approve">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleApprove(adjustment)}
                                  >
                                    <Icon fontSize="small">check</Icon>
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleReject(adjustment)}
                                  >
                                    <Icon fontSize="small">close</Icon>
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(adjustment)}
                                disabled={adjustment.status !== "pending"}
                              >
                                <Icon fontSize="small">edit</Icon>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(adjustment)}
                                disabled={adjustment.status !== "pending"}
                              >
                                <Icon fontSize="small">delete</Icon>
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                component="div"
                count={totalItems}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </>
          ) : (
            <EmptyState
              icon="tune"
              title="No Stock Adjustments Found"
              description="No stock adjustments match your current filters. Try adjusting your search criteria or create a new stock adjustment."
              actionText="Create First Adjustment"
              onAction={handleNewAdjustment}
            />
          )}
        </Paper>

        {/* Stock Adjustment Form */}
        <StockAdjustmentForm
          open={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setSelectedAdjustment(undefined);
          }}
          onSubmit={handleFormSubmit}
          initialData={
            selectedAdjustment
              ? {
                  item_id: selectedAdjustment.item_id,
                  adjustment_type: selectedAdjustment.adjustment_type,
                  quantity: selectedAdjustment.quantity,
                  batch_id: selectedAdjustment.batch_id,
                  warehouse_id: selectedAdjustment.warehouse_id,
                  location_id: selectedAdjustment.location_id,
                  remarks: selectedAdjustment.remarks,
                  adjustment_date: selectedAdjustment.adjustment_date,
                }
              : undefined
          }
          title={
            selectedAdjustment
              ? "Edit Stock Adjustment"
              : "New Stock Adjustment"
          }
          isLoading={formLoading}
          currentUser={currentUser}
        />

        {/* Approve Confirmation Dialog */}
        <ActionConfirmationDialog
          open={approveDialog.open}
          onClose={() =>
            setApproveDialog({ open: false, adjustment: null, loading: false })
          }
          onConfirm={handleConfirmApprove}
          actionType="approve"
          loading={approveDialog.loading}
          requestId={approveDialog.adjustment?.reference_number}
          itemName={approveDialog.adjustment?.item_name}
          itemDescription={
            approveDialog.adjustment?.item_sku
              ? `SKU: ${approveDialog.adjustment.item_sku}`
              : undefined
          }
          customTitle="Approve Stock Adjustment"
          customMessage="Are you sure you want to approve this stock adjustment? Once approved, the inventory levels will be updated according to the adjustment details."
          extraInfo={
            approveDialog.adjustment && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="body2">
                  <strong>Type:</strong>{" "}
                  {getAdjustmentTypeLabel(
                    approveDialog.adjustment.adjustment_type
                  )}
                </Typography>
                <Typography variant="body2">
                  <strong>Quantity:</strong> {approveDialog.adjustment.quantity}{" "}
                  {approveDialog.adjustment.unit}
                </Typography>
                <Typography variant="body2">
                  <strong>Warehouse:</strong>{" "}
                  {approveDialog.adjustment.warehouse_name}
                </Typography>
                {approveDialog.adjustment.remarks && (
                  <Typography variant="body2">
                    <strong>Remarks:</strong> {approveDialog.adjustment.remarks}
                  </Typography>
                )}
              </Box>
            )
          }
        />

        {/* Reject Reason Dialog */}
        <RejectionReasonDialog
          open={rejectDialog.open}
          onClose={() =>
            setRejectDialog({ open: false, adjustment: null, loading: false })
          }
          onConfirm={handleConfirmReject}
          loading={rejectDialog.loading}
          requestId={rejectDialog.adjustment?.reference_number}
          itemName={rejectDialog.adjustment?.item_name}
          title="Reject Stock Adjustment"
          message="You are about to reject this stock adjustment. Please provide a clear reason to help understand why the adjustment was rejected."
          placeholder="Please provide a detailed reason for rejecting this stock adjustment..."
          confirmButtonText="Reject Adjustment"
          loadingText="Rejecting..."
          notificationMessage="The adjustment creator will be notified about this rejection with your reason."
          idLabel="Adjustment ID"
        />
      </Box>
    </LocalizationProvider>
  );
};
